import type { ExerciseTypeConfig } from '../registry/types';
import type { 
  SelectForBlankExerciseDefinition, 
  SelectForBlankExerciseSubmission,
  SelectForBlankEvaluationDetail 
} from '../types';
import { selectForBlankSubmissionSchema } from '../types/schemas';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: SelectForBlankExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * SelectForBlank exercise type configuration
 */
export const selectForBlankConfig: ExerciseTypeConfig<
  'SelectForBlank',
  SelectForBlankExerciseDefinition,
  SelectForBlankExerciseSubmission,
  SelectForBlankEvaluationDetail
> = {
  type: 'SelectForBlank',
  isAutoGradable: true,
  submissionSchema: selectForBlankSubmissionSchema,

  defaultMaxScore: (definition) => {
    const totalBlanks = definition.sentences.reduce(
      (count, sentence) => count + (sentence.blanks?.length ?? 0),
      0
    );
    return getNormalizedMaxScore(definition, Math.max(1, totalBlanks));
  },

  evaluate: (definition, submission) => {
    const expectedSentences = definition.sentences ?? [];
    const providedSelections = submission.selections ?? [];

    const sentenceEvaluations = expectedSentences.map((sentence, sentenceIndex) => {
      const blanks = sentence.blanks ?? [];
      const selections = providedSelections[sentenceIndex] ?? [];

      const blankEvaluations = blanks.map((blank, blankIndex) => {
        const options = blank.options ?? [];
        const correctIndex = options.findIndex((option) => option.isCorrect);
        const selectedIndex = selections[blankIndex] ?? null;
        const isCorrect = selectedIndex !== null && correctIndex >= 0 && selectedIndex === correctIndex;

        return {
          blankId: blank.blankId,
          correctOptionIndex: correctIndex >= 0 ? correctIndex : null,
          selectedOptionIndex: typeof selectedIndex === 'number' && selectedIndex >= 0 ? selectedIndex : null,
          isCorrect,
        };
      });

      return {
        sentenceId: sentence.sentenceId,
        blanks: blankEvaluations,
      };
    });

    const flattenedBlankResults = sentenceEvaluations.flatMap((sentence) => sentence.blanks);
    const totalCorrect = flattenedBlankResults.filter((blank) => blank.isCorrect).length;
    const totalBlanks = flattenedBlankResults.length;
    const maxScore = getNormalizedMaxScore(definition, Math.max(1, totalBlanks));

    return {
      score: totalBlanks === 0 ? 0 : Math.round((totalCorrect / totalBlanks) * maxScore),
      maxScore,
      isFullyCorrect: totalBlanks > 0 && totalCorrect === totalBlanks,
      details: {
        kind: 'SelectForBlank',
        sentences: sentenceEvaluations,
      },
    };
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    const sentences = Array.isArray(details.sentences) ? details.sentences : [];

    return {
      sentences: sentences.map((sentence: any, sentenceIndex: number) => ({
        sentenceId: sentence?.sentenceId,
        sentence: sentence?.sentence ?? '___',
        order: sentence?.order ?? sentenceIndex,
        correction: sentence?.correction ?? '',
        blanks: (Array.isArray(sentence?.blanks) ? sentence.blanks : []).map((blank: any, blankIndex: number) => ({
          blankId: blank?.blankId,
          position: blank?.position ?? blankIndex,
          options: (Array.isArray(blank?.options) ? blank.options : []).map((option: any, optionIndex: number) => ({
            optionId: option?.optionId,
            label: option?.label ?? '',
            isCorrect: Boolean(option?.isCorrect),
            order: option?.order ?? optionIndex,
          })),
        })),
      })),
    };
  },

  createDefault: (order) => ({
    type: 'SelectForBlank' as const,
    sentences: [
      {
        sentenceId: undefined,
        sentence: '___',
        order: 0,
        correction: '',
        blanks: [
          {
            blankId: undefined,
            position: 0,
            options: [
              { optionId: undefined, label: '', isCorrect: true, order: 0 },
              { optionId: undefined, label: '', isCorrect: false, order: 1 },
            ],
          },
        ],
      },
    ],
  }),
};
